import dayjs from "dayjs";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { TextCell } from "@similarweb/pro-logic-components";
import { Popover, PopoverContent, PopoverTrigger } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { CheckMarkCell } from "app/pages/sales-intelligence/common-components/table-cells";
import ColumnsService from "app/pages/sales-intelligence/modules/company-analysis/services/columns/factory";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { DescriptionPopup } from "app/pages/website-analysis/website-technologies/components/DescriptionPopup";
import {
    COLUMN_CATEGORY,
    COLUMN_INSTALLATION_DATE,
    COLUMN_IS_FREE,
    COLUMN_IS_PAID,
    COLUMN_REMOVED_PERIOD,
    COLUMN_STATUS,
    COLUMN_SUB_CATEGORY,
    COLUMN_TECHNOLOGY_NAME,
    COLUMN_WORKING_PERIOD,
} from "app/pages/website-analysis/website-technologies/constants/column-names";
import { SubdomainsPopupContent } from "app/pages/website-analysis/website-technologies/styles";
import { DomainNameAndIcon } from "app/pages/workspace/common/tableAdditionalColumns";
import type { TechnologyItem } from "app/pages/workspace/sales/sub-modules/feed/types/technologies";
import dateTimeService from "app/services/date-time/dateTimeService";
import { WHITESPACE_MMMM_YYYY } from "app/services/date-time/formats";

export const getTechnographicsColumns = (title: () => string): ColumnsType<TechnologyItem> => {

    return [
        {
            dataIndex: COLUMN_TECHNOLOGY_NAME,
            fixed: "left",
            title,
            sorter: ColumnsService.stringSorter(COLUMN_TECHNOLOGY_NAME),
            render: (value: TechnologyItem[typeof COLUMN_TECHNOLOGY_NAME], row: TechnologyItem) => (
                // Forces remount when technology at this row position changes,
                // resetting ImageWithFallback error state in @similarweb/ui-components
                <DescriptionPopup
                    key={row.technologyId}
                    description={row.description}
                    enabled={!!row.description}
                >
                    <DomainNameAndIcon
                        domain={value}
                        icon={salesSettingsHelper.getTechnographicsLink(row.technologyId)}
                    />
                </DescriptionPopup>
            ),
            width: 261,
        },
        {
            dataIndex: COLUMN_CATEGORY,
            title: translate("analysis.overview.technographics.table.category"),
            sorter: ColumnsService.stringSorter(COLUMN_CATEGORY),
            width: 185,
        },
        {
            dataIndex: COLUMN_SUB_CATEGORY,
            title: translate("analysis.overview.technographics.table.sub_category"),
            sorter: ColumnsService.stringSorter(COLUMN_SUB_CATEGORY),
            width: 156,
        },
        {
            dataIndex: COLUMN_STATUS,
            title: translate("analysis.overview.technographics.table.status"),
            sorter: ColumnsService.stringSorter(COLUMN_STATUS),
            render: (value: TechnologyItem[typeof COLUMN_STATUS], row: TechnologyItem) => {
                const isRemoved = value === "removed";

                const subdomains = row.usages
                    .filter((usage) => (isRemoved ? !!usage.removalDate : !usage.removalDate))
                    .map((usage) => usage.site);

                const text = translate(`analysis.overview.technographics.table.${value}`);

                return (
                    <Popover
                        placement="top"
                        withArrow
                        isHover
                    >
                        <PopoverTrigger asChild>
                            <div>{text}</div>
                        </PopoverTrigger>
                        <PopoverContent>
                            <SubdomainsPopupContent>{subdomains.join("\n")}</SubdomainsPopupContent>
                        </PopoverContent>
                    </Popover>
                );
            },
            width: 110,
        },
        {
            dataIndex: COLUMN_WORKING_PERIOD,
            title: translate("analysis.overview.technographics.table.workingPeriod"),
            sorter: ColumnsService.numberSorter(COLUMN_WORKING_PERIOD),
            render: (value: number) => <TextCell>{value ? dayjs().subtract(value, "ms").fromNow(true) : ""}</TextCell>,
            width: 128,
        },
        {
            dataIndex: COLUMN_INSTALLATION_DATE,
            title: translate("analysis.overview.technographics.table.installationDate"),
            sorter: ColumnsService.numberSorter(COLUMN_INSTALLATION_DATE),
            render: (value: number) => {
                const formatted = value ? dateTimeService.formatWithMoment(value, WHITESPACE_MMMM_YYYY) : "";
                return <TextCell>{formatted}</TextCell>;
            },
            width: 128,
        },
        {
            dataIndex: COLUMN_REMOVED_PERIOD,
            title: translate("analysis.overview.technographics.table.removed"),
            sorter: ColumnsService.numberSorter(COLUMN_REMOVED_PERIOD),
            render: (value: number) => (
                <TextCell>{!value || value === 0 ? "-" : dayjs().subtract(value, "ms").fromNow()}</TextCell>
            ),
            width: 128,
        },
        {
            dataIndex: COLUMN_IS_FREE,
            title: translate("analysis.overview.technographics.table.free"),
            sorter: ColumnsService.booleanSorter(COLUMN_IS_FREE),
            render: (value: boolean) => <CheckMarkCell isChecked={value} />,
            width: 70,
            resize: false,
        },
        {
            dataIndex: COLUMN_IS_PAID,
            title: translate("analysis.overview.technographics.table.paid"),
            sorter: ColumnsService.booleanSorter(COLUMN_IS_PAID),
            render: (value: boolean) => <CheckMarkCell isChecked={value} />,
            width: 70,
            resize: false,
        },
    ];
};
